import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { Size } from '../types.js';
import styles from './styles.js';

/**
 * uwc-link
 *
 * An accessible anchor element with an animated underline and optional icon.
 *
 * @attr {string}                        href     - URL the link points to.
 * @attr {string}                        target   - Browsing context (_blank | _self | _parent | _top).
 * @attr {string}                        rel      - Rel attribute. "noopener noreferrer" is auto-added for target="_blank".
 * @attr {string}                        download - Download filename (omit value to use the resource filename).
 * @attr {string}                        icon     - Bootstrap Icon name rendered alongside the label.
 * @attr {'prefix'|'suffix'}             icon-pos - Icon placement. Default: "suffix".
 * @attr {'small'|'medium'|'large'}     size     - Font-size variant.
 * @attr {'primary'|'secondary'|'muted'} variant  - Color variant.
 * @attr {boolean}                       disabled - Disables the link (renders a non-interactive span).
 *
 * @slot (default) - Link label text or mixed content.
 *
 * @cssprop --uwc-link-color           - Link color. Default: var(--uwc-color-primary).
 * @cssprop --uwc-link-font-weight     - Font weight. Default: 700.
 * @cssprop --uwc-link-font-size       - Font size. Default: inherit.
 * @cssprop --uwc-link-secondary-color - Color for the "secondary" variant.
 * @cssprop --uwc-link-muted-color     - Color for the "muted" variant.
 */
export class UwcLink extends LitElement {

  static override styles: CSSResultGroup = styles;

  @property({ reflect: true }) href     = '';
  @property({ reflect: true }) target   = '';
  @property({ reflect: true }) rel      = '';
  @property({ reflect: true }) download = '';
  @property({ reflect: true }) icon     = '';
  @property({ attribute: 'icon-pos', reflect: true }) iconPos: 'prefix' | 'suffix' = 'suffix';
  @property({ reflect: true }) size: Size = 'medium';
  @property({ reflect: true }) variant: 'primary' | 'secondary' | 'muted' = 'primary';
  @property({ type: Boolean, reflect: true }) disabled = false;

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _resolveRel(): string | undefined {
    const parts = this.rel ? this.rel.split(/\s+/) : [];
    if (this.target === '_blank') {
      if (!parts.includes('noopener'))    parts.push('noopener');
      if (!parts.includes('noreferrer')) parts.push('noreferrer');
    }
    return parts.length ? parts.join(' ') : undefined;
  }

  private _icon(pos: 'prefix' | 'suffix') {
    if (!this.icon || this.iconPos !== pos) return nothing;
    return html`
      <uwc-icon
        name="${this.icon}"
        size="1em"
        class="uwc-link__icon uwc-link__icon--${pos}"
        part="icon"
        aria-hidden="true"
      ></uwc-icon>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    if (this.disabled) {
      return html`
        <span
          class="uwc-link uwc-link--disabled"
          role="link"
          aria-disabled="true"
          part="base"
        >
          ${this._icon('prefix')}
          <slot></slot>
          ${this._icon('suffix')}
        </span>
      `;
    }

    const rel = this._resolveRel();

    return html`
      <a
        class="uwc-link"
        href="${ifDefined(this.href || undefined)}"
        target="${ifDefined(this.target || undefined)}"
        rel="${ifDefined(rel)}"
        download="${ifDefined(this.download || undefined)}"
        part="base"
      >
        ${this._icon('prefix')}
        <slot></slot>
        ${this._icon('suffix')}
      </a>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-link': UwcLink; }
}
